import { useEffect, useState } from "react";
import { readJson, writeJson } from "../lib/storage";
import { appDevice } from "../tafo-app/app-storage";

export type Theme = "light" | "dark";
const KEY = "tafo.theme";

const systemTheme = (): Theme => (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light");

/**
 * The theme this device remembers, put on the page before anything is drawn: the opening and the screens
 * before the till (starting, pairing, the terms) follow it too. On a phone with the Tafo app the phone's
 * own, as the till there follows it (public/theme-start.js did the same before the first drawing): it never
 * starts light to turn dark a moment later (Furkan, 18/10/2026: a white flash from Ober to Kassa).
 */
export function applyStoredTheme(): void {
  document.documentElement.dataset.theme = appDevice.read() !== null ? systemTheme() : readJson<Theme>(KEY, "light");
}

/**
 * "Wit" or "Zwart", remembered per device. The till on the owner's phone (in the Tafo app) follows the phone
 * instead, as the Tafo app does, also when it changes at sunset (Furkan, 18/10/2026); the
 * symbol in the bar still switches it until the phone changes again. A till pc keeps its own choice.
 */
export function useTheme(followSystem = false): [Theme, (theme: Theme) => void] {
  const [theme, setTheme] = useState<Theme>(() => (followSystem ? systemTheme() : readJson<Theme>(KEY, "light")));
  useEffect(() => {
    if (!followSystem) return;
    const dark = window.matchMedia("(prefers-color-scheme: dark)");
    const apply = () => {
      setTheme(systemTheme());
    };
    dark.addEventListener("change", apply);
    return () => {
      dark.removeEventListener("change", apply);
    };
  }, [followSystem]);
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    if (!followSystem) writeJson(KEY, theme);
  }, [theme, followSystem]);
  return [theme, setTheme];
}
